import { useEffect, useMemo, useState } from "preact/hooks";
import type { CoreData, SexPref } from "../data/types";
import { useStore } from "../state/store";
import {
  DEFAULT_FILTERS,
  LEN_MAX,
  LEN_MIN,
  filters,
  settings,
  type Character,
  type Filters,
  type Popularity,
  type SortMode,
  type TrendFilter,
} from "../state/app";
import { deckFocus, passes } from "../state/deck";
import { partnerName } from "../state/partner";
import { closeOverlay } from "../state/router";
import { listWords } from "../data/origins";
import { fmt } from "../data/format";
import { Sheet } from "./Sheet";
import { HIDE_TYPES, OriginList, hideStats } from "./pickers";
import { IconChevron } from "./icons";
import { onRadioKey, rovingTab } from "./radio";

export function Chips<T extends string | number>({
  options,
  value,
  onChange,
  multi,
  label,
}: {
  options: { v: T; label: string }[];
  value: T[];
  onChange: (v: T[]) => void;
  multi?: boolean;
  label: string;
}) {
  return (
    <div class="chips" role="group" aria-label={label}>
      {options.map((o) => {
        const on = value.includes(o.v);
        return (
          <button
            key={String(o.v)}
            type="button"
            class={`chip-btn ${on ? "on" : ""}`}
            aria-pressed={on}
            onClick={() => {
              if (multi) onChange(on ? value.filter((x) => x !== o.v) : [...value, o.v]);
              else onChange([o.v]);
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

export function Segmented<T extends string>({
  options,
  value,
  onChange,
  label,
}: {
  options: { v: T; label: string }[];
  value: T;
  onChange: (v: T) => void;
  label: string;
}) {
  return (
    <div class="segmented" role="radiogroup" aria-label={label}>
      {options.map((o, i) => (
        <button
          key={o.v}
          type="button"
          role="radio"
          aria-checked={value === o.v}
          tabIndex={rovingTab(i, value === o.v, options.some((x) => x.v === value))}
          class={value === o.v ? "on" : ""}
          onClick={() => onChange(o.v)}
          onKeyDown={(ev) => onRadioKey(ev, i, options.length, (j) => onChange(options[j].v))}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

const LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

export const POP_OPTIONS: { v: Popularity; label: string }[] = [
  { v: "all", label: "Any" },
  { v: "top100", label: "Top 100" },
  { v: "top500", label: "Top 500" },
  { v: "top1000", label: "Top 1,000" },
  { v: "known", label: "Not too common" },
  { v: "uncommon", label: "Uncommon" },
  { v: "rare", label: "Rare" },
];

export const TREND_OPTIONS: { v: TrendFilter; label: string }[] = [
  { v: "any", label: "Any" },
  { v: "rising", label: "Rising" },
  { v: "steady", label: "Steady" },
  { v: "falling", label: "Falling" },
  { v: "new", label: "New" },
];

/** The style tags this country's records support. */
export function styleOptions(core: CoreData): { v: Character; label: string }[] {
  const out: { v: Character; label: string }[] = [
    { v: "classic", label: "Classic" },
    { v: "timeless", label: "Timeless" },
    { v: "vintage", label: "Vintage revival" },
    { v: "unisex", label: "Unisex" },
  ];
  if (core.country.id === "uk")
    out.push({ v: "scotland", label: "Big in Scotland" }, { v: "ni", label: "Big in Northern Ireland" });
  return out;
}

/** What the chosen trend means, in the records' terms. */
export function trendHint(t: TrendFilter): string {
  if (t === "rising") return "Given to at least 25% more babies than five years earlier.";
  if (t === "falling") return "Given to at least 25% fewer babies than five years earlier.";
  if (t === "steady") return "Within 25% of five years earlier, either way.";
  if (t === "new") return "First recorded in the last five years.";
  return "";
}

const STYLE_HINT: Record<Character, string> = {
  classic: "Classic: in the top 100 for most of the last 30 years.",
  timeless: "Timeless: in the top 100 decade after decade, for most of the last century.",
  vintage: "Vintage revival: top 100 in the early 1900s, faded by the 1960s, and back now.",
  unisex: "Unisex: at least a fifth of the babies given it are girls, and a fifth boys.",
  scotland: "Big in Scotland: at least three times as common there as in England & Wales.",
  ni: "Big in Northern Ireland: at least three times as common there as in England & Wales.",
};

/** What the chosen styles mean (nothing until one is chosen). */
export function styleHint(chosen: Character[]): string {
  return chosen.map((c) => STYLE_HINT[c]).join(" ");
}

export function popHint(core: CoreData, pop: Popularity): string {
  if (pop === "known") return "Ranked 101 to 1,000: familiar, but unlikely to be shared in a class.";
  if (pop === "uncommon") return "Ranked 1,001 to 5,000: you might meet one at nursery.";
  if (pop === "rare") return "Beyond the top 5,000, or too few babies to rank.";
  if (core.country.id === "uk") return "Ranks are for the whole UK, adding together the three nations' records.";
  if (core.country.id === "other") return `Ranks add the UK's and the US's records together, from ${core.latest}.`;
  return `Ranks are for the whole of ${core.country.name}, from ${core.latest}.`;
}

export function LengthRange({ f, set }: { f: Pick<Filters, "lenMin" | "lenMax">; set: (p: Partial<Filters>) => void }) {
  return (
    <div class="range2">
      <label>
        <span>Shortest</span>
        <input
          type="range"
          min={LEN_MIN}
          max={LEN_MAX}
          value={f.lenMin}
          aria-valuetext={`${f.lenMin} letters`}
          onInput={(ev) => {
            const v = Number((ev.target as HTMLInputElement).value);
            set({ lenMin: v, lenMax: Math.max(v, f.lenMax) });
          }}
        />
      </label>
      <label>
        <span>Longest</span>
        <input
          type="range"
          min={LEN_MIN}
          max={LEN_MAX}
          value={f.lenMax}
          aria-valuetext={`${f.lenMax} letters`}
          onInput={(ev) => {
            const v = Number((ev.target as HTMLInputElement).value);
            set({ lenMax: v, lenMin: Math.min(v, f.lenMin) });
          }}
        />
      </label>
    </div>
  );
}

export function lengthLabel(f: Pick<Filters, "lenMin" | "lenMax">): string {
  if (f.lenMin === LEN_MIN && f.lenMax === LEN_MAX) return "any";
  return `${f.lenMin} to ${f.lenMax === LEN_MAX ? `${LEN_MAX}+` : f.lenMax} letters`;
}

function OriginsPage({ core, f, sex, onBack }: { core: CoreData; f: Filters; sex: SexPref; onBack: () => void }) {
  const known = useMemo(
    () => Math.round((core.entries.filter((e) => e.origin).length / core.entries.length) * 100),
    [core],
  );
  return (
    <div class="sheet-subpage">
      <button class="back-btn" onClick={onBack}>
        <IconChevron dir="left" size={20} /> Filters
      </button>
      <p class="fhint">
        Only shows nothing but names from those origins; Hide never shows them. The origin is recorded for {known}% of
        these names, so choosing Only also leaves out every name whose origin isn't known.
      </p>
      <OriginList
        core={core}
        sex={sex}
        only={f.origins}
        hide={f.hideOrigins}
        onChange={(origins, hideOrigins) => filters.set({ origins, hideOrigins })}
      />
    </div>
  );
}

export function FilterSheet({ core, open }: { core: CoreData; open: boolean }) {
  const f = useStore(filters, (s) => s);
  const sex = useStore(settings, (s) => s.sex);
  const [page, setPage] = useState<"main" | "origins">("main");
  useEffect(() => {
    if (!open) setPage("main");
  }, [open]);
  const set = (p: Partial<Filters>) => filters.set(p);

  const count = useMemo(() => {
    let n = 0;
    for (const e of core.entries) if (passes(e, f, sex, core)) n++;
    return n;
  }, [f, sex, core]);
  const stats = useMemo(() => (open ? hideStats(core, sex) : null), [core, sex, open]);

  const focus = f.collection === "partner" ? `${partnerName()}'s picks` : deckFocus(f);
  const searchBits: string[] = [];
  if (f.nameText.trim())
    searchBits.push(
      `${f.nameMode === "contains" ? "containing" : f.nameMode === "ends" ? "ending in" : "starting with"} “${f.nameText.trim()}”`,
    );
  if (f.meaningText.trim()) searchBits.push(`meaning “${f.meaningText.trim()}”`);

  return (
    <Sheet
      open={open}
      onClose={closeOverlay}
      title={page === "origins" ? "Origins" : "Filters"}
      footer={
        <div class="row-gap foot-row">
          <button
            class="btn btn-ghost"
            onClick={() =>
              filters.set({
                ...DEFAULT_FILTERS,
                hide: f.hide,
                hideOrigins: f.hideOrigins,
              })
            }
          >
            Reset
          </button>
          <button class="btn btn-primary btn-grow" onClick={closeOverlay} disabled={count === 0}>
            {count === 0 ? "No names match" : `Show ${fmt(count)} name${count === 1 ? "" : "s"}`}
          </button>
        </div>
      }
    >
      {page === "origins" ? (
        <OriginsPage core={core} f={f} sex={sex} onBack={() => setPage("main")} />
      ) : (
        <>
          {focus && (
            <div class="filter-banner">
              <span>
                Swiping <b>{focus}</b>
              </span>
              <button class="btn btn-small" onClick={() => set({ collection: null, list: null })}>
                Show all names
              </button>
            </div>
          )}
          {searchBits.length > 0 && (
            <div class="filter-banner">
              <span>Names {searchBits.join(" and ")}</span>
              <button class="btn btn-small" onClick={() => set({ nameText: "", meaningText: "" })}>
                Clear
              </button>
            </div>
          )}

          <section class="fsec">
            <h3>Names for</h3>
            <Segmented<SexPref>
              label="Names for"
              value={sex}
              onChange={(v) => settings.set({ sex: v })}
              options={[
                { v: "F", label: "A girl" },
                { v: "M", label: "A boy" },
                { v: "X", label: "Either" },
              ]}
            />
          </section>

          <section class="fsec">
            <h3>Popularity in {core.latest}</h3>
            <Chips<Popularity>
              label="Popularity"
              value={[f.pop]}
              onChange={([v]) => set({ pop: v })}
              options={POP_OPTIONS}
            />
            <p class="fhint">{popHint(core, f.pop)}</p>
          </section>

          <section class="fsec">
            <h3>Trend over five years</h3>
            <Chips<TrendFilter>
              label="Trend"
              value={[f.trend]}
              onChange={([v]) => set({ trend: v })}
              options={TREND_OPTIONS}
            />
            {f.trend !== "any" && <p class="fhint">{trendHint(f.trend)}</p>}
          </section>

          <section class="fsec">
            <h3>Origin</h3>
            <button class="pick-row" onClick={() => setPage("origins")}>
              <span class="pick-label">Only show</span>
              <span class="pick-value">{f.origins.length ? listWords(f.origins) : "Any origin"}</span>
              <IconChevron dir="right" size={18} />
            </button>
            <button class="pick-row" onClick={() => setPage("origins")}>
              <span class="pick-label">Hide</span>
              <span class="pick-value">{f.hideOrigins.length ? listWords(f.hideOrigins) : "None"}</span>
              <IconChevron dir="right" size={18} />
            </button>
          </section>

          <section class="fsec">
            <h3>Leave out</h3>
            {HIDE_TYPES.map((t) => {
              const st = stats?.[t.id];
              if (st && !st.n && !f.hide.includes(t.id)) return null;
              const on = f.hide.includes(t.id);
              return (
                <label class="toggle-row" key={t.id}>
                  <span>
                    <span class="toggle-label">{t.label}</span>
                    {st && (
                      <span class="toggle-hint">
                        {st.ex.length ? `${st.ex.join(", ")}: ` : ""}
                        {fmt(st.n)} name{st.n === 1 ? "" : "s"}
                      </span>
                    )}
                  </span>
                  <input
                    type="checkbox"
                    role="switch"
                    checked={on}
                    onChange={(ev) =>
                      set({
                        hide: (ev.target as HTMLInputElement).checked
                          ? [...f.hide, t.id]
                          : f.hide.filter((h) => h !== t.id),
                      })
                    }
                  />
                  <span class="switch" aria-hidden="true" />
                </label>
              );
            })}
          </section>

          <section class="fsec">
            <h3>Style</h3>
            <Chips<Character>
              label="Style"
              multi
              value={f.character}
              onChange={(v) => set({ character: v })}
              options={styleOptions(core)}
            />
            {f.character.length > 0 && <p class="fhint">{styleHint(f.character)}</p>}
          </section>

          <section class="fsec">
            <div class="fsec-head">
              <h3>Starts with</h3>
              {f.letters.length > 0 && (
                <button class="link-btn" onClick={() => set({ letters: [] })}>
                  Clear
                </button>
              )}
            </div>
            <div class="letter-grid" role="group" aria-label="First letter">
              {LETTERS.map((L) => {
                const on = f.letters.includes(L);
                return (
                  <button
                    key={L}
                    class={`letter ${on ? "on" : ""}`}
                    aria-pressed={on}
                    onClick={() =>
                      set({
                        letters: on ? f.letters.filter((x) => x !== L) : [...f.letters, L],
                      })
                    }
                  >
                    {L}
                  </button>
                );
              })}
            </div>
          </section>

          <section class="fsec">
            <h3>
              Length: <span class="fval">{lengthLabel(f)}</span>
            </h3>
            <LengthRange f={f} set={set} />
          </section>

          <section class="fsec">
            <h3>Syllables</h3>
            <Chips<number>
              label="Syllables"
              multi
              value={f.syl}
              onChange={(v) => set({ syl: v })}
              options={[
                { v: 1, label: "1" },
                { v: 2, label: "2" },
                { v: 3, label: "3" },
                { v: 4, label: "4+" },
              ]}
            />
          </section>

          <section class="fsec">
            <h3>Order</h3>
            <Chips<SortMode>
              label="Order"
              value={[f.sort]}
              onChange={([v]) => set({ sort: v })}
              options={[
                { v: "smart", label: "Mixed" },
                { v: "popular", label: "Most popular" },
                { v: "rarest", label: "Rarest first" },
                { v: "rising", label: "Fastest rising" },
                { v: "az", label: "A to Z" },
                { v: "shuffle", label: "Random" },
              ]}
            />
            <p class="fhint">
              {f.sort === "smart"
                ? "Popular names turn up more often, with plenty of rarer ones mixed in."
                : f.sort === "shuffle"
                  ? "Every name equally likely, so expect lots of rare ones."
                  : ""}
            </p>
          </section>
        </>
      )}
    </Sheet>
  );
}
